import { Box, CircularProgress, Stack } from '@mui/material';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getDocument } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import PdfPage from './PdfPage';
import PdfViewerToolbar, { type ZoomLevel } from './PdfViewerToolbar';
import printPdf from './printPdf';
import './pdfWorker';
import './textLayer.css';

interface Props {
    pdfUrl?: string;
    /** Suggested filename when the user clicks the viewer toolbar Download button. */
    downloadFileName?: string;
}

const DEFAULT_DOWNLOAD_FILE_NAME = 'Legal_Document.pdf';

export default function PdfViewer({ pdfUrl, downloadFileName }: Props) {
    if (!pdfUrl) return null;

    return (
        // Remount the whole viewer when the PDF URL changes (e.g. after signing).
        <PdfViewerInner
            key={pdfUrl}
            pdfUrl={pdfUrl}
            downloadFileName={downloadFileName ?? DEFAULT_DOWNLOAD_FILE_NAME}
        />
    );
}

const CONTAINER_PADDING = 16;
const PAGE_GAP = 16;
const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4];
const FULLSCREEN_CHANGE_EVENTS = ['fullscreenchange', 'webkitfullscreenchange'] as const;

interface FullscreenDocument extends Document {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
}

interface FullscreenElement extends HTMLElement {
    webkitRequestFullscreen?: () => Promise<void> | void;
}

const CAN_USE_FULLSCREEN = canUseFullscreen();

function canUseFullscreen(): boolean {
    if (typeof document === 'undefined') return false;
    const element = document.createElement('div') as FullscreenElement;
    return (
        typeof element.requestFullscreen === 'function' ||
        typeof element.webkitRequestFullscreen === 'function'
    );
}

function getFullscreenElement(): Element | null {
    const doc = document as FullscreenDocument;
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function requestElementFullscreen(element: HTMLElement) {
    const fullscreenElement = element as FullscreenElement;
    if (typeof fullscreenElement.requestFullscreen === 'function') {
        void fullscreenElement.requestFullscreen();
        return;
    }
    if (typeof fullscreenElement.webkitRequestFullscreen === 'function') {
        void fullscreenElement.webkitRequestFullscreen();
    }
}

function exitDocumentFullscreen() {
    const doc = document as FullscreenDocument;
    if (typeof doc.exitFullscreen === 'function') {
        void doc.exitFullscreen();
        return;
    }
    if (typeof doc.webkitExitFullscreen === 'function') {
        void doc.webkitExitFullscreen();
    }
}

interface Size {
    width: number;
    height: number;
}

interface InnerProps {
    pdfUrl: string;
    downloadFileName: string;
}

function PdfViewerInner({ pdfUrl, downloadFileName }: InnerProps) {
    const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [basePageSize, setBasePageSize] = useState<Size | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState<ZoomLevel>('page-width');
    const [containerSize, setContainerSize] = useState<Size | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);

    // Load the document and measure the first page (used for fit-to-width/page).
    useEffect(() => {
        let cancelled = false;
        const loadingTask = getDocument({ url: pdfUrl });

        (async () => {
            const loadedPdf = await loadingTask.promise;
            const firstPage = await loadedPdf.getPage(1);
            if (cancelled) return;
            const viewport = firstPage.getViewport({ scale: 1 });
            setBasePageSize({ width: viewport.width, height: viewport.height });
            setPdf(loadedPdf);
        })().catch((error: unknown) => {
            if (cancelled) return;
            console.error('Failed to load PDF:', error);
            setLoadFailed(true);
        });

        return () => {
            cancelled = true;
            void loadingTask.destroy();
        };
    }, [pdfUrl]);

    // Track the scroll container size so fit zoom modes follow resizes.
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const observer = new ResizeObserver(() => {
            setContainerSize({ width: scroller.clientWidth, height: scroller.clientHeight });
        });
        observer.observe(scroller);
        return () => observer.disconnect();
    }, []);

    // Keep the current page anchored at the top of the view when zoom changes.
    const currentPageRef = useRef(currentPage);
    currentPageRef.current = currentPage;
    const lastScaleRef = useRef<number | null>(null);

    useEffect(() => {
        if (!CAN_USE_FULLSCREEN) return;
        function handleFullscreenChange() {
            setIsFullscreen(getFullscreenElement() === rootRef.current);
        }
        for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
            document.addEventListener(eventName, handleFullscreenChange);
        }
        return () => {
            for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
                document.removeEventListener(eventName, handleFullscreenChange);
            }
        };
    }, []);

    let scale = 1;
    if (typeof zoom === 'number') {
        scale = zoom;
    } else if (basePageSize && containerSize) {
        const availableWidth = containerSize.width - CONTAINER_PADDING * 2;
        const availableHeight = containerSize.height - CONTAINER_PADDING * 2;
        scale =
            zoom === 'page-width'
                ? availableWidth / basePageSize.width
                : Math.min(
                      availableWidth / basePageSize.width,
                      availableHeight / basePageSize.height,
                  );
        scale = Math.max(scale, 0.1);
    }

    useEffect(() => {
        // Skip the initial measurement; only re-anchor on actual zoom changes.
        const isFirstMeasurement = lastScaleRef.current === null;
        const scaleChanged = lastScaleRef.current !== scale;
        lastScaleRef.current = scale;
        if (isFirstMeasurement || !scaleChanged) return;

        const pageElement = getPageElement(currentPageRef.current);
        const scroller = scrollerRef.current;
        if (pageElement && scroller) {
            scroller.scrollTop = pageElement.offsetTop - CONTAINER_PADDING;
        }
    });

    function getPageElement(pageNumber: number): HTMLElement | null {
        return scrollerRef.current?.querySelector(`[data-pdf-page="${pageNumber}"]`) ?? null;
    }

    function goToPage(pageNumber: number) {
        if (!pdf) return;
        const clamped = Math.min(Math.max(pageNumber, 1), pdf.numPages);
        const pageElement = getPageElement(clamped);
        const scroller = scrollerRef.current;
        if (pageElement && scroller) {
            scroller.scrollTop = pageElement.offsetTop - CONTAINER_PADDING;
        }
        setCurrentPage(clamped);
    }

    function handleScroll() {
        const scroller = scrollerRef.current;
        if (!scroller || !pdf) return;
        // Current page = page with the most visible height (ties go to the earlier page).
        const viewTop = scroller.scrollTop;
        const viewBottom = viewTop + scroller.clientHeight;
        let visiblePage = 1;
        let bestVisibleHeight = 0;
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
            const pageElement = getPageElement(pageNumber);
            if (!pageElement) continue;
            const top = pageElement.offsetTop;
            const bottom = top + pageElement.offsetHeight;
            const visibleHeight = Math.min(bottom, viewBottom) - Math.max(top, viewTop);
            if (visibleHeight > bestVisibleHeight) {
                bestVisibleHeight = visibleHeight;
                visiblePage = pageNumber;
            }
        }
        setCurrentPage(visiblePage);
    }

    function zoomIn() {
        setZoom(
            ZOOM_STEPS.find((step) => step > scale + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1],
        );
    }

    function zoomOut() {
        const smallerSteps = ZOOM_STEPS.filter((step) => step < scale - 0.001);
        setZoom(smallerSteps.length > 0 ? smallerSteps[smallerSteps.length - 1] : ZOOM_STEPS[0]);
    }

    function toggleFullscreen() {
        if (!rootRef.current || !CAN_USE_FULLSCREEN) return;
        if (getFullscreenElement()) {
            exitDocumentFullscreen();
        } else {
            requestElementFullscreen(rootRef.current);
        }
    }

    async function handleDownload() {
        if (!pdf) return;
        try {
            const data = await pdf.getData();
            const blob = new Blob([data as BlobPart], { type: 'application/pdf' });
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = downloadFileName;
            anchor.click();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error('Failed to download PDF:', error);
        }
    }

    async function handlePrint() {
        if (!pdf || isPrinting) return;
        setIsPrinting(true);
        try {
            await printPdf(pdf);
        } catch (error) {
            console.error('Failed to print PDF:', error);
        } finally {
            setIsPrinting(false);
        }
    }

    if (loadFailed) {
        return (
            <Box sx={{ p: '1rem', textAlign: 'center' }}>
                Error loading document. Please try again or contact your law firm if the issue
                persists.
            </Box>
        );
    }

    return (
        <Box
            ref={rootRef}
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fff',
            }}
        >
            <Box sx={{ p: '4px', borderBottom: '1px solid #ccc' }}>
                <PdfViewerToolbar
                    currentPage={currentPage}
                    numPages={pdf?.numPages ?? 0}
                    zoomPercent={Math.round(scale * 100)}
                    disabled={!pdf}
                    isFullscreen={isFullscreen}
                    showFullscreenButton={CAN_USE_FULLSCREEN}
                    isPrinting={isPrinting}
                    onGoToPage={goToPage}
                    onZoomIn={zoomIn}
                    onZoomOut={zoomOut}
                    onSelectZoom={setZoom}
                    onToggleFullscreen={toggleFullscreen}
                    onDownload={() => void handleDownload()}
                    onPrint={() => void handlePrint()}
                />
            </Box>
            <Box
                ref={scrollerRef}
                onScroll={handleScroll}
                sx={{
                    position: 'relative',
                    flex: 1,
                    overflow: 'auto',
                    // Rendering pages/text layers mutates the DOM; without this the
                    // browser's scroll anchoring fights programmatic scrolling.
                    overflowAnchor: 'none',
                    backgroundColor: '#eee',
                }}
            >
                {pdf && basePageSize ? (
                    <Box
                        sx={{
                            width: 'fit-content',
                            margin: '0 auto',
                            padding: `${CONTAINER_PADDING}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: `${PAGE_GAP}px`,
                        }}
                    >
                        {Array.from({ length: pdf.numPages }, (_, index) => (
                            <PdfPage
                                key={index + 1}
                                pdf={pdf}
                                pageNumber={index + 1}
                                scale={scale}
                                estimatedWidth={basePageSize.width * scale}
                                estimatedHeight={basePageSize.height * scale}
                            />
                        ))}
                    </Box>
                ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                        <CircularProgress />
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
